/**
 * Common Utility Services & Reusable UI Component Triggers
 * Provides toasts, loading screen toggles, reusable modal launchers, and formatters.
 */

import { Modal, Tab } from 'bootstrap';

// Cache references
let globalModalInstance = null;

/* ==========================================
   1. GLOBAL FULLSCREEN LOADER
   ========================================== */

/**
 * Show global app-loading screen overlay
 */
export function showLoader() {
  const loader = document.getElementById('global-loader');
  if (loader) {
    loader.classList.remove('fade-out');
  }
}

/**
 * Hide global app-loading screen overlay
 */
export function hideLoader() {
  const loader = document.getElementById('global-loader');
  if (loader) {
    loader.classList.add('fade-out');
  }
}


/* ==========================================
   2. TOAST NOTIFICATIONS
   ========================================== */

/**
 * Trigger animated Toast notification in bottom right
 * @param {string} title - Heading of the toast
 * @param {string} message - Body description text
 * @param {'success'|'warning'|'error'|'info'} type - Toast theme type
 */
export function showToast(title, message, type = 'success') {
  const container = document.getElementById('toast-container');
  if (!container) return;

  // Map icon classes matching the theme
  const icons = {
    success: 'bi-check-circle-fill',
    warning: 'bi-exclamation-triangle-fill',
    error: 'bi-exclamation-octagon-fill',
    info: 'bi-info-circle-fill'
  };

  const toastId = 'toast-' + Date.now();
  const toastHtml = `
    <div id="${toastId}" class="toast-custom toast-${type}" role="alert" aria-live="assertive" aria-atomic="true">
      <i class="bi ${icons[type] || 'bi-info-circle-fill'} toast-custom-icon"></i>
      <div class="toast-custom-body">
        <div class="toast-custom-title font-heading">${title}</div>
        <div class="toast-custom-text">${message}</div>
      </div>
      <button type="button" class="toast-custom-close" data-toast-close aria-label="Close">
        <i class="bi bi-x"></i>
      </button>
    </div>
  `;

  // Append new toast to container
  container.insertAdjacentHTML('beforeend', toastHtml);
  const element = document.getElementById(toastId);

  // Setup auto-cleanup after 4 seconds
  const autoRemoveTimer = setTimeout(() => {
    removeToast(element);
  }, 4000);

  // Bind close click event listener
  element.querySelector('[data-toast-close]').addEventListener('click', () => {
    clearTimeout(autoRemoveTimer);
    removeToast(element);
  });
}

/**
 * Smoothly remove toast item from DOM
 * @param {HTMLElement} element 
 */
function removeToast(element) {
  if (!element) return;
  element.style.animation = 'slideInRight 0.3s reverse forwards';
  element.addEventListener('animationend', () => {
    element.remove();
  }, { once: true });
}


/* ==========================================
   3. REUSABLE GLOBAL MODAL
   ========================================== */

/**
 * Populate and launch the pre-defined Bootstrap modal component
 * @param {string} title - Header text
 * @param {string} bodyHtml - HTML string representing modal body
 * @param {string} [footerHtml] - Optional HTML string for modal actions (buttons)
 * @param {object} [options] - Bootstrap Modal configuration options
 */
export function openModal(title, bodyHtml, footerHtml = '', options = {}) {
  const modalEl = document.getElementById('global-modal');
  if (!modalEl) return;

  // Update elements
  document.getElementById('global-modal-title').textContent = title;
  document.getElementById('global-modal-body').innerHTML = bodyHtml;
  
  const footerEl = document.getElementById('global-modal-footer');
  if (footerHtml) {
    footerEl.innerHTML = footerHtml;
    footerEl.classList.remove('d-none');
  } else {
    footerEl.innerHTML = '';
    footerEl.classList.add('d-none');
  }

  // Handle initialization/instantiation
  if (!globalModalInstance) {
    globalModalInstance = new Modal(modalEl, options);
  }
  
  globalModalInstance.show();
}

/**
 * Close/hide the global modal component
 */
export function closeModal() {
  if (globalModalInstance) {
    globalModalInstance.hide();
  }
}


/* ==========================================
   4. DATA FORMATTERS
   ========================================== */

export function getCurrencyCode() {
  return localStorage.getItem('pharma-currency-symbol') || 'INR';
}

export function getCurrencySymbol() {
  const currency = getCurrencyCode();
  if (currency === 'EUR') return '€';
  if (currency === 'USD') return '$';
  return '₹';
}

/**
 * Format raw number amount into localized currency format
 * @param {number} value 
 * @returns {string}
 */
export function formatCurrency(value) {
  const currency = getCurrencyCode();
  if (isNaN(value)) {
    return `${getCurrencySymbol()}0.00`;
  }
  
  let locale = 'en-IN';
  if (currency === 'USD') locale = 'en-US';
  else if (currency === 'EUR') locale = 'de-DE';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(value);
}

/**
 * Format Javascript date object or timestamp to readable string
 * @param {Date|number|string} dateValue 
 * @param {boolean} includeTime 
 * @returns {string}
 */
export function formatDate(dateValue, includeTime = false) {
  if (!dateValue) return 'N/A';
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return 'Invalid Date';

  const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  if (includeTime) {
    dateOptions.hour = '2-digit';
    dateOptions.minute = '2-digit';
  }
  return date.toLocaleDateString('en-US', dateOptions);
}

/* ==========================================
   5. GEOLOCATION & GEOCONDING HELPERS
   ========================================== */

/**
 * Fetches the user's current GPS location coordinates.
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export function getCurrentCoordinates() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        let msg = "Could not get your location. Please turn on location/GPS access.";
        if (error.code === error.PERMISSION_DENIED) {
          msg = "Location permission denied. Please allow location access to log visits.";
        }
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

/**
 * Calculates distance (in meters) between two GPS points using the Haversine formula.
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Earth radius in meters
  const p1 = lat1 * Math.PI / 180;
  const p2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(p1) * Math.cos(p2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Fetches human-readable address name using coordinates and Google Maps Geocoding API.
 */
export async function fetchLocationName(lat, lng) {
  const apiKey = "AIzaSyBHY2n8pPqVcza9bxN70qXe-4KKGU46r68";
  const googleUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
  
  try {
    const response = await fetch(googleUrl);
    const data = await response.json();
    if (data.status === "OK" && data.results.length > 0) {
      return data.results[0].formatted_address;
    }
    console.warn("Google Maps Geocoding status was not OK, trying Nominatim fallback. Status:", data.status);
  } catch (error) {
    console.warn("Google Geocoding request failed, trying Nominatim fallback. Error:", error);
  }

  // FALLBACK: Use OpenStreetMap Nominatim API (free, open, no key required)
  const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`;
  try {
    const response = await fetch(nominatimUrl, {
      headers: {
        'Accept-Language': 'en'
      }
    });
    const data = await response.json();
    if (data && data.display_name) {
      return data.display_name;
    }
  } catch (error) {
    console.error("Nominatim fallback failed:", error);
  }

  // Fallback to coordinates format if everything fails
  return `Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`;
}

